import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Currency = "USD" | "EUR" | "GBP" | "JPY" | "KRW";

interface CurrencyRates {
  USD: number;
  EUR: number;
  GBP: number;
  JPY: number;
  KRW: number;
}

const defaultRates: CurrencyRates = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.50,
  KRW: 1350,
};

const currencySymbols: Record<Currency, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  KRW: "₩",
};

const currencyLocales: Record<Currency, string> = {
  USD: "en-US",
  EUR: "de-DE",
  GBP: "en-GB",
  JPY: "ja-JP",
  KRW: "ko-KR",
};

// Map country codes to currencies
const countryToCurrency: Record<string, Currency> = {
  US: "USD",
  GB: "GBP",
  UK: "GBP",
  DE: "EUR",
  FR: "EUR",
  IT: "EUR",
  ES: "EUR",
  NL: "EUR",
  BE: "EUR",
  AT: "EUR",
  IE: "EUR",
  PT: "EUR",
  GR: "EUR",
  FI: "EUR",
  JP: "JPY",
  KR: "KRW",
};

// Module-level cache to deduplicate currency rate fetches
let ratesCache: { data: CurrencyRates; timestamp: number } | null = null;
const RATES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let ratesInflight: Promise<CurrencyRates> | null = null;

export const useCurrency = () => {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    const stored = localStorage.getItem("currency");
    return (stored as Currency) || "USD";
  });
  const [rates, setRates] = useState<CurrencyRates>(() => {
    return ratesCache?.data ?? defaultRates;
  });
  const [loading, setLoading] = useState(!ratesCache);
  const [detected, setDetected] = useState(false);

  // Fetch rates from database (deduplicated)
  useEffect(() => {
    const fetchRates = async () => {
      // Check cache
      if (ratesCache && Date.now() - ratesCache.timestamp < RATES_CACHE_TTL) {
        setRates(ratesCache.data);
        setLoading(false);
        return;
      }

      // Deduplicate in-flight
      if (!ratesInflight) {
        ratesInflight = (async () => {
          try {
            const { data, error } = await supabase
              .from("site_settings")
              .select("value")
              .eq("key", "currency_rates")
              .single();

            if (!error && data?.value && typeof data.value === 'object') {
              const value = data.value as Record<string, number>;
              const parsed: CurrencyRates = {
                USD: value.USD ?? defaultRates.USD,
                EUR: value.EUR ?? defaultRates.EUR,
                GBP: value.GBP ?? defaultRates.GBP,
                JPY: value.JPY ?? defaultRates.JPY,
                KRW: value.KRW ?? defaultRates.KRW,
              };
              ratesCache = { data: parsed, timestamp: Date.now() };
              return parsed;
            }
          } catch {
            // Use defaults
          } finally {
            ratesInflight = null;
          }
          ratesCache = { data: defaultRates, timestamp: Date.now() };
          return defaultRates;
        })();
      }

      try {
        const result = await ratesInflight;
        if (result) setRates(result);
      } catch {
        // defaults already set
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  // Auto-detect location on first visit using browser timezone
  useEffect(() => {
    const detectCurrency = () => {
      const stored = localStorage.getItem("currency");
      if (stored) {
        setDetected(true);
        return;
      }

      try {
        // Use timezone-based detection (no external API needed)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let detectedCurrency: Currency = "USD"; // default
        
        if (timezone.startsWith("Europe/London") || timezone.startsWith("Europe/Belfast")) {
          detectedCurrency = "GBP";
        } else if (timezone.startsWith("Europe/")) {
          detectedCurrency = "EUR";
        } else if (timezone.startsWith("Asia/Tokyo")) {
          detectedCurrency = "JPY";
        } else if (timezone.startsWith("Asia/Seoul")) {
          detectedCurrency = "KRW";
        }
        
        setCurrencyState(detectedCurrency);
        localStorage.setItem("currency", detectedCurrency);
      } catch {
        // Silently fail - USD will be used as default
      } finally {
        setDetected(true);
      }
    };

    detectCurrency();
  }, []);

  const setCurrency = useCallback((curr: Currency) => {
    setCurrencyState(curr);
    localStorage.setItem("currency", curr);
  }, []);

  // Format price in selected currency
  const formatPrice = useCallback(
    (priceInUSD: number): string => {
      const rate = rates[currency] || 1;
      const convertedPrice = priceInUSD * rate;

      // For JPY and KRW, don't show decimals
      const maximumFractionDigits = ["JPY", "KRW"].includes(currency) ? 0 : 2;

      return new Intl.NumberFormat(currencyLocales[currency], {
        style: "currency",
        currency: currency,
        maximumFractionDigits,
      }).format(convertedPrice);
    },
    [currency, rates]
  );

  // Format price always in USD (for cart and checkout consistency)
  const formatPriceUSD = useCallback(
    (priceInUSD: number): string => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(priceInUSD);
    },
    []
  );

  // Get just the converted amount (for calculations)
  const convertPrice = useCallback(
    (priceInUSD: number): number => {
      const rate = rates[currency] || 1;
      return priceInUSD * rate;
    },
    [currency, rates]
  );

  return {
    currency,
    setCurrency,
    formatPrice,
    formatPriceUSD,
    convertPrice,
    symbol: currencySymbols[currency],
    loading,
    detected,
  };
};

export const currencyOptions: { code: Currency; label: string; symbol: string }[] = [
  { code: "USD", label: "US Dollar", symbol: "$" },
  { code: "EUR", label: "Euro", symbol: "€" },
  { code: "GBP", label: "British Pound", symbol: "£" },
  { code: "JPY", label: "Japanese Yen", symbol: "¥" },
  { code: "KRW", label: "Korean Won", symbol: "₩" },
];
